import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Crown, Check, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PricingPage() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const response = await axios.get(`${API}/tiers`);
      setTiers(response.data);
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTier = (tierId) => {
    if (!user) {
      navigate('/register');
    } else {
      navigate(`/checkout/${tierId}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gold-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass-panel">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-gold-400" />
              <span className="font-heading text-xl font-bold text-white">House of Royalty</span>
            </Link>
            
            {user ? (
              <Link to="/dashboard" className="btn-outline text-sm py-2 px-6">
                Dashboard
              </Link>
            ) : (
              <Link to="/login" className="btn-gold text-sm py-2 px-6">
                Sign In
              </Link>
            )}
          </div>
        </div>
      </nav>

      {/* Pricing Section */}
      <section className="pt-32 pb-24 md:pb-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-gold-400 uppercase tracking-[0.3em] text-sm mb-4">Choose Your Path</p>
            <h1 className="text-4xl md:text-6xl font-heading font-bold tracking-tight text-white mb-6">
              Membership <span className="gold-text">Tiers</span>
            </h1>
            <p className="text-zinc-400 max-w-xl mx-auto">
              Select the membership level that matches your ambitions. All tiers include instant access to our exclusive Telegram community.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {tiers.map((tier, index) => (
              <div
                key={tier.id}
                className={`tier-card relative animate-fade-in ${tier.is_popular ? 'popular' : ''}`}
                style={{ animationDelay: `${0.1 * (index + 1)}s` }}
                data-testid={`tier-card-${tier.name.toLowerCase()}`}
              >
                {tier.is_popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="badge badge-gold">Most Popular</span>
                  </div>
                )}
                
                <div className="mb-8">
                  <h3 className="text-2xl font-heading font-semibold text-white mb-2">{tier.name}</h3>
                  <p className="text-zinc-500 text-sm">{tier.description}</p>
                </div>
                
                <div className="mb-8">
                  <span className="text-5xl font-heading font-bold gold-text">${tier.price}</span>
                  <span className="text-zinc-500">/month</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-gold-400 flex-shrink-0 mt-0.5" />
                      <span className="text-zinc-300 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => handleSelectTier(tier.id)}
                  className={`w-full flex items-center justify-center gap-2 ${
                    tier.is_popular ? 'btn-gold' : 'btn-outline'
                  }`}
                  data-testid={`select-tier-${tier.name.toLowerCase()}`}
                >
                  Select {tier.name}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {tiers.length === 0 && (
            <div className="text-center py-16">
              <p className="text-zinc-500">No membership tiers available yet.</p>
              <p className="text-zinc-600 text-sm mt-2">Check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-heading font-semibold text-white mb-12 text-center">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-6">
            {[
              {
                q: 'How do I access the Telegram group?',
                a: 'After completing your payment, you\'ll receive a unique invite link on your dashboard. Click it to join our exclusive community instantly.'
              },
              {
                q: 'Can I cancel my subscription?',
                a: 'Yes, you can cancel anytime from your dashboard. You\'ll retain access until the end of your billing period.'
              },
              {
                q: 'What payment methods do you accept?',
                a: 'We accept all major payment methods through PayPal, including credit cards, debit cards, and PayPal balance.'
              },
              {
                q: 'Can I upgrade my tier later?',
                a: 'Absolutely! You can upgrade your membership tier at any time from your dashboard.'
              }
            ].map((faq, index) => (
              <div key={index} className="glass-panel p-6">
                <h3 className="text-white font-medium mb-2">{faq.q}</h3>
                <p className="text-zinc-500 text-sm">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <Crown className="w-6 h-6 text-gold-400" />
              <span className="font-heading text-lg text-white">House of Royalty</span>
            </div>
            <p className="text-zinc-600 text-sm">
              &copy; {new Date().getFullYear()} House of Royalty. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
